import { User } from "@/schema"
import { Avatar, AvatarFallback, AvatarImage } from "@ui/shad"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"

import { useFetchUsers } from "@/hooks/api/users"

import { cn } from "../_ui/utils"

const textVariants = cva("ml-2 truncate", {
  variants: {
    size: {
      md: "text-sm",
      sm: "text-sm",
      xs: "text-xs",
    },
  },
  defaultVariants: {
    size: "md",
  },
})

interface UserAvatarProps extends VariantProps<typeof textVariants> {
  userId?: string
  user?: Pick<User, "id" | "full_name" | "profile_picture_url">
  className?: string
  showName?: boolean
  variant?: "default" | "clickable-pill" | "pill"
  onRemove?: (userId: string) => void
  twoInitials?: boolean
}

const SYSTEM_USER = {
  id: "system",
  full_name: "Slant",
  profile_picture_url: "/icon-with-padding.png",
} as User

const UNASSIGNED_USER = {
  id: "unassigned",
  full_name: "Unassigned",
} as User

export function UserAvatar({
  userId,
  user: providedUser,
  size = "sm",
  className = "",
  showName,
  variant = "default",
  onRemove,
  twoInitials = false,
}: UserAvatarProps) {
  const { data: usersData } = useFetchUsers(providedUser ? "" : undefined)
  let user = providedUser || (!userId ? SYSTEM_USER : null)
  if (!providedUser && userId === "unassigned") {
    user = UNASSIGNED_USER
  } else if (!providedUser && userId) {
    user = usersData?.users?.find((u) => u.id === userId) || SYSTEM_USER
  }
  if (!user) return null

  const initials =
    twoInitials || (size !== "sm" && size !== "xs")
      ? user.full_name
          ?.split(" ")
          .map((name: string) => name[0])
          .join("")
          .slice(0, 2) || ""
      : user.full_name?.charAt(0) || ""

  const avatarEl = (
    <Avatar size={size} className={className}>
      <AvatarImage src={user.profile_picture_url} alt={user.full_name || ""} className="object-cover" />
      <AvatarFallback
        className={cn(
          "bg-tertiary m-0 flex h-full w-full items-center justify-center overflow-hidden rounded-full p-0 font-medium",
          twoInitials ? "px-0.5 text-[10px] leading-none tracking-tight" : "text-xs"
        )}
      >
        {userId === "unassigned" ? "?" : initials}
      </AvatarFallback>
    </Avatar>
  )

  if (showName) {
    return (
      <div
        className={cn(
          "text-tertiary-foreground inline-flex min-w-0 items-center gap-0 text-sm font-medium",
          variant.includes("pill") && "border-border bg-background rounded-full border pr-2",
          variant === "clickable-pill" && "hover:bg-accent cursor-pointer",
          onRemove && userId && "pr-1"
        )}
      >
        {avatarEl}
        <span className={textVariants({ size })}>{user.full_name}</span>
        {onRemove && userId && (
          <button
            onClick={() => onRemove(userId)}
            className="text-muted-foreground hover:text-destructive ml-0.5 shrink-0"
          >
            <X className="size-3" />
          </button>
        )}
      </div>
    )
  }

  return (
    <>
      <div className="relative">
        {avatarEl}
        {onRemove && userId && (
          <button
            onClick={() => onRemove(userId)}
            className="bg-primary text-primary-foreground hover:bg-destructive absolute -top-0.5 -right-0.5 hidden size-3 items-center justify-center rounded-full opacity-0 transition-colors duration-200 group-hover:flex group-hover:opacity-90"
          >
            <X className="size-2" />
          </button>
        )}
      </div>
    </>
  )
}
